/**
 * Schedule-conflict vocabulary shared by every scheduling surface (dispatch
 * board, calendar, booking sheet, job visit forms).
 *
 * The conflict engine (POST /conflicts/check, and the 409 body of every staff
 * write) reports a flat list of `{ type, severity, message }`. The UI groups
 * them into three human buckets — Busy / Out of area / Missing skill — and a
 * staff user acknowledges the lot before the save goes through with
 * `overrideConflicts: true`. Everything in here is pure so the same grouping
 * renders identically in a banner, a dialog and a read-only "acknowledged
 * issues" note.
 */

export type ConflictSeverity = 'BLOCK' | 'WARN' | 'INFO';

export interface ScheduleConflict {
  type: string;
  details?: Record<string, unknown>;
  severity: ConflictSeverity;
  message: string;
  /** Set when the caller can attribute the conflict to one crew member. */
  technicianId?: string;
  /** Stamped on a saved entry once the technician's data answered it. */
  resolvedAt?: string;
  /** Stamped on a saved entry once someone put it away by hand. */
  dismissedAt?: string;
}

/**
 * A saved entry that has been answered or put away.
 *
 * Cleared entries are STAMPED rather than deleted, because the saved note is
 * what authorises the visit's overlap in the database — drop the
 * `TECH_DOUBLE_BOOKED` entry and the next write to the visit is refused as a
 * double-booking. They stay in the record and out of the UI.
 */
export function isClearedConflict(conflict: ScheduleConflict): boolean {
  return Boolean(conflict.resolvedAt || conflict.dismissedAt);
}

export type ConflictBucketKey = 'busy' | 'area' | 'skill' | 'other';

export const CONFLICT_TYPE_LABELS: Record<string, string> = {
  ARRIVAL_WINDOW_VIOLATED: 'Arrival window',
  BLACKOUT_DATE: 'Blackout date',
  OUTSIDE_BUSINESS_HOURS: 'Outside business hours',
  SERVICE_BLACKOUT_DATE: 'Service blackout',
  TEAM_CAPACITY_EXCEEDED: 'Team capacity',
  TECH_CAPACITY_EXCEEDED: 'Technician capacity',
  TECH_CERT_EXPIRED: 'Certification expired',
  TECH_DOUBLE_BOOKED: 'Double-booked',
  TECH_OUTSIDE_HOURS: 'Technician outside hours',
  TECH_SKILL_MISMATCH: 'Skill mismatch',
  TECH_TERRITORY_MISMATCH: 'Territory mismatch',
  TECH_TIME_OFF: 'Technician time off',
  TIME_BLOCK_FULL: 'Time block full',
  TRAVEL_INFEASIBLE_FROM_PREV: 'Travel infeasible from previous stop',
  TRAVEL_INFEASIBLE_TO_NEXT: 'Travel infeasible to next stop',
};

export const CONFLICT_BUCKETS: Record<ConflictBucketKey, { label: string; types: string[] }> = {
  busy: {
    label: 'Busy or unavailable',
    types: [
      'TECH_DOUBLE_BOOKED',
      'TECH_TIME_OFF',
      'TECH_OUTSIDE_HOURS',
      'TECH_CAPACITY_EXCEEDED',
      'TEAM_CAPACITY_EXCEEDED',
      'TIME_BLOCK_FULL',
      'OUTSIDE_BUSINESS_HOURS',
      'BLACKOUT_DATE',
      'SERVICE_BLACKOUT_DATE',
    ],
  },
  area: {
    label: 'Out of area',
    types: ['TECH_TERRITORY_MISMATCH', 'TRAVEL_INFEASIBLE_FROM_PREV', 'TRAVEL_INFEASIBLE_TO_NEXT'],
  },
  skill: {
    label: 'Missing skill or certification',
    types: ['TECH_SKILL_MISMATCH', 'TECH_CERT_EXPIRED'],
  },
  other: {
    label: 'Other',
    types: ['ARRIVAL_WINDOW_VIOLATED'],
  },
};

/** Display order of the buckets — and the order `summarizeConflicts` joins them in. */
export const CONFLICT_BUCKET_ORDER: ConflictBucketKey[] = ['busy', 'area', 'skill', 'other'];

/** Short labels for the summary line ("Busy · Out of area"). */
const BUCKET_SHORT_LABELS: Record<ConflictBucketKey, string> = {
  busy: 'Busy',
  area: 'Out of area',
  skill: 'Missing skill',
  other: 'Other',
};

const TYPE_TO_BUCKET: Map<string, ConflictBucketKey> = new Map(
  CONFLICT_BUCKET_ORDER.flatMap((key) =>
    CONFLICT_BUCKETS[key].types.map((type) => [type, key] as const),
  ),
);

/** Severity → row/chip classes (lifted from the booking sheet's ConflictPanel). */
export const SEVERITY_CLASSES: Record<ConflictSeverity, string> = {
  BLOCK: 'text-destructive border-destructive/30 bg-destructive/10',
  WARN: 'text-warning border-warning/30 bg-warning/10',
  INFO: 'text-muted-foreground border-border bg-muted/40',
};

/** Severity → dot colour for the compact bucket rows. */
export const SEVERITY_DOT_CLASSES: Record<ConflictSeverity, string> = {
  BLOCK: 'bg-destructive',
  WARN: 'bg-warning',
  INFO: 'bg-muted-foreground',
};

export function conflictLabel(type: string): string {
  const label = CONFLICT_TYPE_LABELS[type];
  if (label) return label;
  return type
    .split('_')
    .filter(Boolean)
    .map((part) => part.charAt(0) + part.slice(1).toLowerCase())
    .join(' ');
}

export function conflictBucket(type: string): ConflictBucketKey {
  return TYPE_TO_BUCKET.get(type) ?? 'other';
}

export interface ConflictBucketGroup {
  key: ConflictBucketKey;
  label: string;
  conflicts: ScheduleConflict[];
}

export interface TechnicianConflictGroup {
  /** `null` when the conflicts aren't attributed to a specific technician. */
  technicianId: string | null;
  technicianName: string | null;
  buckets: ConflictBucketGroup[];
}

/** Key for the unattributed group (the engine reports crew-wide conflicts without a tech). */
const UNATTRIBUTED = '__all__';

/**
 * Group conflicts per technician (attributed first, in first-seen order, then
 * the unattributed remainder), and within each technician per bucket in
 * display order. Empty buckets are dropped.
 */
export function groupConflicts(
  conflicts: ScheduleConflict[],
  techNameOf?: (technicianId: string) => string | undefined,
): TechnicianConflictGroup[] {
  const byTech = new Map<string, ScheduleConflict[]>();
  for (const conflict of conflicts) {
    const key = conflict.technicianId ?? UNATTRIBUTED;
    const list = byTech.get(key);
    if (list) list.push(conflict);
    else byTech.set(key, [conflict]);
  }
  const keys = [...byTech.keys()].filter((key) => key !== UNATTRIBUTED);
  if (byTech.has(UNATTRIBUTED)) keys.push(UNATTRIBUTED);

  return keys.map((key) => {
    const list = byTech.get(key) ?? [];
    const buckets = CONFLICT_BUCKET_ORDER.map((bucketKey) => ({
      key: bucketKey,
      label: CONFLICT_BUCKETS[bucketKey].label,
      conflicts: list.filter((conflict) => conflictBucket(conflict.type) === bucketKey),
    })).filter((bucket) => bucket.conflicts.length > 0);
    const technicianId = key === UNATTRIBUTED ? null : key;
    return {
      technicianId,
      technicianName: technicianId ? (techNameOf?.(technicianId) ?? null) : null,
      buckets,
    };
  });
}

/** A staff save needs an explicit acknowledgement for any BLOCK or WARN; INFO is display-only. */
export function requiresAcknowledgement(conflicts: ScheduleConflict[]): boolean {
  return conflicts.some(
    (conflict) => conflict.severity === 'BLOCK' || conflict.severity === 'WARN',
  );
}

/** Distinct bucket short-labels joined with " · " — "Busy · Out of area". */
export function summarizeConflicts(conflicts: ScheduleConflict[]): string {
  const present = new Set(conflicts.map((conflict) => conflictBucket(conflict.type)));
  return CONFLICT_BUCKET_ORDER.filter((key) => present.has(key))
    .map((key) => BUCKET_SHORT_LABELS[key])
    .join(' · ');
}

export interface ConflictErrorPayload {
  message: string;
  conflicts: ScheduleConflict[];
  hasBlocking: boolean;
  /**
   * False when the server refuses this write whatever the user acknowledges —
   * e.g. an overlap while the tenant's "Allow overlapping schedules" is off.
   * Offering "Schedule anyway" for it can only fail.
   */
  overridable: boolean;
}

function isSeverity(value: unknown): value is ConflictSeverity {
  return value === 'BLOCK' || value === 'WARN' || value === 'INFO';
}

function coerceConflicts(raw: unknown): ScheduleConflict[] | null {
  if (!Array.isArray(raw)) return null;
  const conflicts: ScheduleConflict[] = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const candidate = entry as Record<string, unknown>;
    if (typeof candidate.type !== 'string' || typeof candidate.message !== 'string') continue;
    // Unknown severities are kept (as WARN) so a future engine level still asks
    // for an acknowledgement rather than vanishing from the list.
    const severity = isSeverity(candidate.severity) ? candidate.severity : 'WARN';
    conflicts.push({
      type: candidate.type,
      severity,
      message: candidate.message,
      ...(candidate.details &&
      typeof candidate.details === 'object' &&
      !Array.isArray(candidate.details)
        ? { details: candidate.details as Record<string, unknown> }
        : {}),
      ...(typeof candidate.technicianId === 'string'
        ? { technicianId: candidate.technicianId }
        : {}),
    });
  }
  return conflicts;
}

/**
 * Recognise the 409 a staff write returns when the conflict engine wants an
 * acknowledgement: `{ message, hasBlocking, conflicts: [...] }`, or refuses the
 * write outright (`overridable: false`). Accepts the
 * normalised `ApiError` (`.status` + `.data`) and a raw axios error
 * (`.response.status` + `.response.data`). Anything else — a different status,
 * a 409 without a conflicts array — is `null`, and the caller should treat the
 * error as a plain failure.
 */
export function parseConflictError(err: unknown): ConflictErrorPayload | null {
  if (!err || typeof err !== 'object') return null;
  const candidate = err as {
    status?: unknown;
    data?: unknown;
    message?: unknown;
    response?: { status?: unknown; data?: unknown };
  };
  const status = candidate.status ?? candidate.response?.status;
  if (status !== 409) return null;
  const body = (candidate.data ?? candidate.response?.data) as
    | { message?: unknown; conflicts?: unknown; hasBlocking?: unknown; overridable?: unknown }
    | null
    | undefined;
  if (!body || typeof body !== 'object') return null;
  const conflicts = coerceConflicts(body.conflicts);
  if (!conflicts) return null;
  const message =
    typeof body.message === 'string'
      ? body.message
      : typeof candidate.message === 'string'
        ? candidate.message
        : 'This time conflicts with existing work.';
  return {
    message,
    conflicts,
    hasBlocking:
      typeof body.hasBlocking === 'boolean'
        ? body.hasBlocking
        : conflicts.some((conflict) => conflict.severity === 'BLOCK'),
    overridable: body.overridable !== false,
  };
}

export interface PreflightKeyInput {
  technicianIds: readonly string[];
  startAt?: string | null;
  endAt?: string | null;
  serviceTypeId?: string | null;
  jobTypeId?: string | null;
  jobId?: string | null;
  excludeAppointmentId?: string | null;
  zipCode?: string | null;
  address?: string | null;
  businessUnitId?: string | null;
  promisedStartAt?: string | null;
  promisedEndAt?: string | null;
}

/**
 * Stable identity of one preflight request — technician order is irrelevant
 * (sorted), blank optionals collapse to the same key. Used both as the query
 * key and as the "what did the user acknowledge" stamp, so changing the tech
 * or the time resets the acknowledgement.
 */
export function preflightRequestKey(input: PreflightKeyInput): string {
  // The FIRST technician is the lead (it is sent as `technicianId` and is the
  // one skill/area-gated), so its identity is part of the key: swapping
  // [leadA, helperB] → [leadB, helperA] is a different request and must not
  // reuse the cached result or the previous acknowledgement. Helpers are
  // order-insensitive.
  const unique = [...new Set(input.technicianIds.filter(Boolean))];
  const [lead, ...helpers] = unique;
  const techs = lead ? [lead, ...helpers.sort()] : [];
  return [
    techs.join(','),
    input.startAt ?? '',
    input.endAt ?? '',
    input.serviceTypeId ?? '',
    input.jobTypeId ?? '',
    input.jobId ?? '',
    input.excludeAppointmentId ?? '',
    input.zipCode ?? '',
    input.address ?? '',
    input.businessUnitId ?? '',
    input.promisedStartAt ?? '',
    input.promisedEndAt ?? '',
  ].join('|');
}
